import { Box, Flex, Text, Spinner } from '@chakra-ui/react';
import { captureException } from '@sentry/react';
import { useEffect, useState } from 'react';
import {
  IntegrationService,
  useOauthAuthenticationUrlQuery,
  DataConnectorJobStatus,
} from 'src/api/queries';
import { useAppContext } from 'src/contexts/AppContext';
import { Icon } from 'src/components/atoms/Icon';
import { ShopDomainModal } from 'src/features/Settings/components/molecules/ShopDomainModal';

type DataConnectorProps = {
  integration: IntegrationService;
  connected: boolean;
  isLoading?: boolean;
  displayName?: string;
  icon?: React.ReactNode;
  onError?: (title: string, description: string) => void;
  showFetchStatus?: boolean;
  jobStatus?: DataConnectorJobStatus;
};

export const DataConnector: React.FC<DataConnectorProps> = ({
  integration,
  connected,
  isLoading = false,
  displayName,
  icon,
  onError,
  showFetchStatus = false,
  jobStatus,
}) => {
  const { currentCustomer } = useAppContext();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isShopDomainOpen, setIsShopDomainOpen] = useState(false);
  const [shopDomain, setShopDomain] = useState<string | undefined>(undefined);
  const [isShopConnectPending, setIsShopConnectPending] = useState(false);

  const { refetch } = useOauthAuthenticationUrlQuery({
    customer: currentCustomer?.customerName ?? '',
    service: integration,
    shop_domain: shopDomain,
  });

  const startOauthFlow = async () => {
    setIsConnecting(true);
    try {
      const res = await refetch();
      const url = res.data?.authorizationUrl;
      if (!url) {
        throw new Error(
          `Did not receive authorization url for integration ${integration}`,
        );
      }
      window.open(url, 'oauth', 'width=500,height=700');
    } catch (error) {
      captureException({ error, fn: 'onIntegrationClick' });

      // Show error toast if callback is provided
      if (onError) {
        const connectorDisplayName = displayName || integration;
        onError(
          `Failed to connect to ${connectorDisplayName}`,
          'Please try again or contact support if the issue persists',
        );
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const onIntegrationClick = async () => {
    if (isConnecting || isLoading) return;

    // Shopify's OAuth flow runs on the shop's own domain, so the store
    // address must be collected before the popup can be opened
    if (integration === 'shopify') {
      setIsShopDomainOpen(true);
      return;
    }

    await startOauthFlow();
  };

  const handleShopDomainConnect = (selectedShopDomain: string) => {
    // The OAuth flow starts from the effect below, once the query hook has
    // re-rendered with the new shop domain (refetch has no way to take params)
    setShopDomain(selectedShopDomain);
    setIsShopDomainOpen(false);
    setIsShopConnectPending(true);
  };

  useEffect(() => {
    if (!isShopConnectPending || !shopDomain) return;

    setIsShopConnectPending(false);
    void startOauthFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShopConnectPending, shopDomain]);

  const finalDisplayName = displayName;
  const isActionInProgress = isConnecting || isLoading;

  // Determine fetch status
  const fetchStatus = jobStatus?.status;
  const hasError = fetchStatus === 'error';
  const isFetching = fetchStatus === 'fetching';

  // Determine what status to show
  // If showFetchStatus is false (Connect step): Just show Connected/Not Connected
  // If showFetchStatus is true (Preparing step): Show fetch status only for connected connectors
  let statusDisplay;

  if (isActionInProgress) {
    // Connecting in progress
    statusDisplay = (
      <Flex align="center" borderRadius="md">
        <Spinner size="sm" color="icon.blue" mr={2} />
        <Text fontSize="sm" color="icon.blue" fontWeight="normal">
          Connecting...
        </Text>
      </Flex>
    );
  } else if (!showFetchStatus) {
    // Connect Channels step - simple Connected or Not Connected
    if (connected) {
      statusDisplay = (
        <Flex align="center" px={3} py={1}>
          <Box w={2} h={2} bg="icon.green" borderRadius="full" mr={2} />
          <Text fontSize="sm" color="icon.green" fontWeight="normal">
            Connected
          </Text>
        </Flex>
      );
    } else {
      // Not connected - show chevron arrow
      statusDisplay = (
        <Icon iconName="chevron-right" boxSize={5} color="gray.500" />
      );
    }
  } else {
    // Preparing Data step - show only Connected or Failed status
    // Priority: Failed (if error or not connected) > Connected (if connected and no error)
    if (!connected || hasError) {
      // Not connected or has error - show Failed status (default)
      statusDisplay = (
        <Flex align="center" px={3} py={1} borderRadius="md">
          <Box w={2} h={2} bg="border.red" borderRadius="full" mr={2} />
          <Text fontSize="sm" color="border.red" fontWeight="normal" mr={2}>
            Failed
          </Text>
          <Icon iconName="refresh" boxSize={4} color="text.title" />
        </Flex>
      );
    } else if (isFetching) {
      // Show Fetching status with progress
      statusDisplay = (
        <Flex align="center" px={3} py={1}>
          <Spinner size="xs" color="icon.blue" mr={2} />
          <Text fontSize="sm" color="icon.blue" fontWeight="normal">
            Fetching...
          </Text>
        </Flex>
      );
    } else {
      // Show Connected status (connected and no errors)
      statusDisplay = (
        <Flex align="center" px={3} py={1}>
          <Box w={2} h={2} bg="icon.green" borderRadius="full" mr={2} />
          <Text fontSize="sm" color="icon.green" fontWeight="normal">
            Connected
          </Text>
        </Flex>
      );
    }
  }

  return (
    <Box mb={2}>
      <Box
        bg="surface.control"
        px={4}
        py={4}
        w="full"
        _hover={!isActionInProgress ? { bg: 'surface.controlHover' } : {}}
        cursor={!isActionInProgress ? 'pointer' : 'default'}
        onClick={!isActionInProgress ? onIntegrationClick : undefined}
        borderRadius="md">
        <Flex align="center" justify="space-between">
          <Flex align="center">
            {icon}
            <Text
              ml={4}
              fontFamily="marketingPage"
              fontSize="md"
              color="text.darkGray"
              fontWeight="normal">
              {finalDisplayName}
            </Text>
          </Flex>
          <Flex align="center">{statusDisplay}</Flex>
        </Flex>
      </Box>

      {integration === 'shopify' && (
        <ShopDomainModal
          isOpen={isShopDomainOpen}
          onClose={() => setIsShopDomainOpen(false)}
          onConnect={handleShopDomainConnect}
          isConnecting={isConnecting}
        />
      )}
    </Box>
  );
};
